// frontend/src/components/OpacitySlider.jsx

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Range, getTrackBackground } from "react-range";
import "../styles/OpacitySlider.css";

const OpacitySlider = ({
  layerId,
  initialOpacity = 100,
  onOpacityChange,
}) => {
  const [opacityValue, setOpacityValue] = useState(initialOpacity);
  const [activeThumb, setActiveThumb] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (
      typeof initialOpacity === "number" &&
      !isNaN(initialOpacity) &&
      initialOpacity >= 0 &&
      initialOpacity <= 100
    ) {
      setOpacityValue(initialOpacity);
    } else {
      setOpacityValue(100); // Default value if initialOpacity is invalid
      console.warn(
        `OpacitySlider: Invalid initialOpacity for layer "${layerId}": ${initialOpacity}. Defaulting to 100.`
      );
    }
  }, [initialOpacity, layerId]);

  const handleOpacityChangeInternal = (newValue) => {
    if (
      typeof newValue === "number" &&
      !isNaN(newValue) &&
      newValue >= 0 &&
      newValue <= 100
    ) {
      console.log(
        `OpacitySlider: Changing opacity for layer "${layerId}" to ${newValue}`
      );
      setOpacityValue(newValue);
      onOpacityChange(newValue); // Pass only newOpacity
      setIsDragging(true);
    } else {
      console.error(
        `OpacitySlider: Invalid opacity value received for layer "${layerId}": ${newValue}`
      );
    }
  };

  const handleFinalChange = () => {
    setIsDragging(false);
  };

  const handleThumbEvent = (thumbIndex, eventType) => {
    switch (eventType) {
      case "mouseenter":
      case "focus":
        setActiveThumb(thumbIndex);
        break;
      case "mouseleave":
      case "blur":
        setActiveThumb(null);
        break;
      default:
        break;
    }
  };

  return (
    <div className="opacity-slider-wrapper">
      <div className="opacity-slider-container">
        <span className="slider-label left" aria-label="Minimum opacity">
          Opacity: 0%
        </span>
        <Range
          values={[opacityValue]}
          step={1}
          min={0}
          max={100}
          onChange={(values) => handleOpacityChangeInternal(values[0])}
          onFinalChange={handleFinalChange}
          renderTrack={({ props, children }) => (
            <div
              onMouseDown={props.onMouseDown}
              onTouchStart={props.onTouchStart}
              style={{
                ...props.style,
                height: "10px",
                display: "flex",
                flex: 1,
              }}
            >
              <div
                ref={props.ref}
                style={{
                  height: "2px",
                  width: "100%",
                  borderRadius: "2.5px",
                  background: getTrackBackground({
                    values: [opacityValue],
                    colors: ["#ccc", "#808080", "#ccc"],
                    min: 0,
                    max: 100,
                  }),
                  alignSelf: "center",
                  position: "relative",
                }}
              >
                {children}
              </div>
            </div>
          )}
          renderThumb={({ index, props }) => {
            const { key, ...otherProps } = props;
            return (
              <div
                key={key}
                {...otherProps}
                className={`v-slider__thumb-label pin-thumb ${
                  activeThumb === index ? "active" : ""
                } ${isDragging ? "is-dragging" : ""}`}
                aria-label="Opacity Thumb"
                onMouseEnter={() =>
                  handleThumbEvent(index, "mouseenter")
                }
                onMouseLeave={() =>
                  handleThumbEvent(index, "mouseleave")
                }
                onFocus={() => handleThumbEvent(index, "focus")}
                onBlur={() => handleThumbEvent(index, "blur")}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveThumb(activeThumb === index ? null : index);
                }}
              >
                {(activeThumb === index || isDragging) && (
                  <div className="pin">
                    <div className="pin-value">{opacityValue}</div>
                  </div>
                )}
              </div>
            );
          }}
        />
        <span className="slider-label right">100%</span>
      </div>
    </div>
  );
};

OpacitySlider.propTypes = {
  layerId: PropTypes.string.isRequired,
  initialOpacity: PropTypes.number,
  onOpacityChange: PropTypes.func.isRequired,
};

export default OpacitySlider;
